/**
 * Blank face charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nails_shades`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const blankFaceChartsPack: ToilePack = {
    slug: 'blank-face-charts',
    category: 'beauty',
    name: 'Blank face charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'front', label: 'Front face', subject: 'a blank front face chart with closed eyes and neutral lips, outlines only'},
        {key: 'profile', label: 'Profile', subject: 'a blank profile face chart with the cheekbone and jaw marked lightly'},
        {key: 'eyes', label: 'Eye close-up', subject: 'a blank chart of two large eyes and brows for practising eye looks'},
    ],
    styles: [
        {key: 'line', label: 'Clean line', style: 'clean line art in one uniform thin black weight, nothing filled', palette: 'black ink only', framing: 'drawn large and centered on the page with an even white margin'},
        {key: 'pencil', label: 'Pencil sketch', style: 'a light pencil sketch with a soft graphite line and the faintest tone along the bone structure', palette: 'pale graphite gray only', framing: 'drawn large and centered on the page with an even white margin'},
        {key: 'fashion', label: 'Fashion sketch', style: 'an elegant fashion illustration in a tapered ink brush line, long lashes and a graceful hairline', palette: 'black ink only', framing: 'drawn large on the page with a generous white margin and the hair left as a simple outline'},
    ],
    directions: ['male face', 'bridal look', 'Halloween'],
    directionHint: 'try: male face',
    seo: {
        title: 'Blank Face Charts: Front, Profile, Eye Close-Up',
        metaDescription: 'Printable blank face charts: a front face, a profile and an eye close-up, in clean line, pencil or fashion sketch styles.',
        h1: 'Blank face charts: front, profile, eye close-up',
        tagline: 'Nine blank practice pages: three views, each in a clean line, a pencil sketch and a fashion sketch.',
        intro: 'Blank face charts are empty practice sheets for makeup artists and students. The nine pages show a front face with closed eyes and neutral lips, a profile with the cheekbone and jaw marked lightly, and a close-up of two large eyes and brows. Every area is left empty for makeup, so you can plan a look on paper first. Type a different face or look, such as male face, bridal look or Halloween, and the same drawing style carries it.',
        useCases: [
            'a practice sheet for a makeup course',
            'a face chart for a client consultation',
            'an eye look practice sheet',
            'a sketch sheet for a Halloween look',
        ],
        faq: [
            {q: 'Can I print them?', a: 'Yes. They are printable: each is a plain white sheet with outlines only and no text.'},
            {q: 'What are the three pages?', a: 'A front face, a profile, and a close-up of two large eyes and brows.'},
            {q: 'How do the three drawing styles differ?', a: 'The clean line is one even black weight with nothing filled. The pencil sketch has a light graphite line and faint tone along the bone structure. The fashion sketch is a tapered ink brush line with long lashes.'},
            {q: 'Can I ask for a male face or a bridal look?', a: 'Yes. Type it as a request. The page is not promised to match it exactly.'},
        ],
    },
}
